/**
 * User Management API Service
 */

import { apiClient, APIResponse } from './client';
import {
  UserProfile, UserUpdate, Role, AcceptTermsRequest, AcceptTermsResponse,
  UserAdminProfile, AdminInviteRequest, AdminUpdateRequest,
  McpKeyMetadata, McpKeyCreateResponse,
} from './types';

export class UserService {
  /**
   * Get current user's profile
   */
  async getMyProfile(): Promise<APIResponse<UserProfile>> {
    return apiClient.get<UserProfile>('/api/v1/users/me');
  }

  /**
   * Update current user's profile
   */
  async updateMyProfile(data: UserUpdate): Promise<APIResponse<UserProfile>> {
    return apiClient.put<UserProfile>('/api/v1/users/me', data);
  }

  /**
   * Get user profile by UID
   */
  async getUserProfile(uid: string): Promise<APIResponse<UserProfile>> {
    return apiClient.get<UserProfile>(`/api/v1/users/${uid}`);
  }

  /**
   * Update user profile by UID
   */
  async updateUserProfile(uid: string, data: UserUpdate): Promise<APIResponse<UserProfile>> {
    return apiClient.put<UserProfile>(`/api/v1/users/${uid}`, data);
  }

  /**
   * Get available roles
   */
  async getAvailableRoles(): Promise<APIResponse<Role[]>> {
    return apiClient.get<Role[]>('/api/v1/users/roles/available');
  }

  /**
   * Accept Terms of Service
   */
  async acceptTerms(data: AcceptTermsRequest): Promise<APIResponse<AcceptTermsResponse>> {
    return apiClient.post<AcceptTermsResponse>('/api/v1/users/accept-terms', data);
  }

  // ========== Admin User Management ==========

  async adminListUsers(): Promise<APIResponse<UserAdminProfile[]>> {
    return apiClient.get<UserAdminProfile[]>('/api/v1/users/admin/list');
  }

  async inviteUser(data: AdminInviteRequest): Promise<APIResponse<Record<string, unknown>>> {
    return apiClient.post<Record<string, unknown>>('/api/v1/users/admin/invite', data);
  }

  async adminUpdateUser(uid: string, data: AdminUpdateRequest): Promise<APIResponse<Record<string, unknown>>> {
    return apiClient.put<Record<string, unknown>>(`/api/v1/users/admin/${uid}`, data);
  }

  async deactivateUser(uid: string): Promise<APIResponse<Record<string, unknown>>> {
    return apiClient.post<Record<string, unknown>>(`/api/v1/users/admin/${uid}/deactivate`);
  }

  async reactivateUser(uid: string): Promise<APIResponse<Record<string, unknown>>> {
    return apiClient.post<Record<string, unknown>>(`/api/v1/users/admin/${uid}/reactivate`);
  }

  async resetUserPassword(uid: string): Promise<APIResponse<Record<string, unknown>>> {
    return apiClient.post<Record<string, unknown>>(`/api/v1/users/admin/${uid}/reset-password`);
  }

  async toggleMfa(uid: string, mfaRequired: boolean): Promise<APIResponse<Record<string, unknown>>> {
    return apiClient.post<Record<string, unknown>>(`/api/v1/users/admin/${uid}/toggle-mfa`, { mfaRequired });
  }

  // ========== Self-Service Password ==========

  /**
   * Log a password change event for audit purposes (called after Firebase SDK updatePassword)
   */
  async logPasswordChange(): Promise<APIResponse<null>> {
    return apiClient.post<null>('/api/v1/users/me/password-changed');
  }

  /**
   * Request a password reset email for the current user (non-MFA flow)
   */
  async requestPasswordReset(): Promise<APIResponse<{ email: string }>> {
    return apiClient.post<{ email: string }>('/api/v1/users/me/request-password-reset');
  }

  // ========== Dev Admin: Client Switcher ==========

  async getAvailableClients(): Promise<APIResponse<{ availableClients: { id: string; name: string }[]; currentClientId: string }>> {
    return apiClient.get<{ availableClients: { id: string; name: string }[]; currentClientId: string }>('/api/v1/users/me/available-clients');
  }

  async switchClient(clientId: string): Promise<APIResponse<{ organizationId: string }>> {
    return apiClient.post<{ organizationId: string }>('/api/v1/users/me/switch-client', { clientId });
  }

  // ========== MCP Key Management (KAN-275) ==========

  async createMcpKey(uid: string, scopes: string[]): Promise<APIResponse<McpKeyCreateResponse>> {
    return apiClient.post<McpKeyCreateResponse>(`/api/v1/users/admin/${uid}/mcp-key`, { scopes });
  }

  async getMcpKey(uid: string): Promise<APIResponse<McpKeyMetadata>> {
    return apiClient.get<McpKeyMetadata>(`/api/v1/users/admin/${uid}/mcp-key`);
  }

  async updateMcpKey(uid: string, data: { scopes?: string[]; isActive?: boolean; entityMaskingEnabled?: boolean }): Promise<APIResponse<McpKeyMetadata>> {
    return apiClient.patch<McpKeyMetadata>(`/api/v1/users/admin/${uid}/mcp-key`, data);
  }

  async deleteMcpKey(uid: string): Promise<APIResponse<{ message: string }>> {
    return apiClient.delete<{ message: string }>(`/api/v1/users/admin/${uid}/mcp-key`);
  }

  async rotateMcpKey(uid: string, scopes: string[]): Promise<APIResponse<McpKeyCreateResponse>> {
    return apiClient.post<McpKeyCreateResponse>(`/api/v1/users/admin/${uid}/mcp-key/rotate`, { scopes });
  }
}

export const userService = new UserService();